import { getPreferredLanguage } from '../../../i18n/language';
import { getProductLibraryTemplate } from '../../../i18n/templates/productLibrary';

export enum BatteryLevelEnum {
  NONE = 'NONE',
  LESS_THAN_100WH = 'SMALL',
  MORE_THAN_100WH = 'LARGE'
}

export enum MagneticLevelEnum {
  NONE = 'NONE',
  WEAK = 'WEAK',
  STRONG = 'STRONG'
}

export enum SensitivityLevelEnum {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  REJECTED = 'REJECTED',
  NORMAL = 'NORMAL'
}

export interface ProductData {
  product_id: number;
  user_id: number;
  picture: string | null;
  name: string;
  description?: string;
  weight: number;
  length: number;
  width: number;
  height: number;
  dangerous_products: boolean | null;
  flammable_products: boolean | null;
  export_restricted_products: boolean | null;
  battery_level: BatteryLevelEnum | null;
  magnetic_level: MagneticLevelEnum | null;
  if_extra_packaging: boolean | null;
  other_complicated_handling: string | null;
  categories: string[] | null;
  sensitivity_level: SensitivityLevelEnum | null;
}

export interface Product {
  id: number;
  name: string;
  description?: string;
  weight: number;  // in pounds
  length: number;  // in inches
  width: number;   // in inches
  height: number;  // in inches
  picture?: string;
  dangerous_products?: boolean;
  flammable_products?: boolean;
  export_restricted_products?: boolean;
  battery_level?: BatteryLevelEnum;
  magnetic_level?: MagneticLevelEnum;
  sensitivity_level?: SensitivityLevelEnum;
  if_extra_packaging?: boolean;
  other_complicated_handling?: string;
  categories?: string[];
}

export const parseProduct = (product: ProductData): Product => ({
  id: product.product_id,
  name: product.name,
  description: product.description || undefined,
  weight: product.weight,
  length: product.length,
  width: product.width,
  height: product.height,
  picture: product.picture || undefined,
  dangerous_products: product.dangerous_products || undefined,
  flammable_products: product.flammable_products || undefined,
  export_restricted_products: product.export_restricted_products || undefined,
  battery_level: product.battery_level || undefined,
  magnetic_level: product.magnetic_level || undefined,
  sensitivity_level: product.sensitivity_level || undefined,
  if_extra_packaging: product.if_extra_packaging || undefined,
  other_complicated_handling: product.other_complicated_handling || undefined,
  categories: product.categories || undefined
});

/**
 * Parses product data from backend response and converts it to UI format
 * Formats dimensions and handles null values appropriately
 */
export const parseProductData = (products: ProductData[]): Product[] => {
  if (!products || products.length === 0) {
    return [];
  }

  return products.map(parseProduct);
};

/**
 * Type definitions for product API response
 */

export interface SensitivityConfig {
  label: string;
  className: string;
  tooltip: string;
}

/**
 * Get sensitivity level configuration for UI display
 */
export const getSensitivityConfig = (level?: SensitivityLevelEnum): SensitivityConfig => {
  const text = getProductLibraryTemplate(getPreferredLanguage());
  const tooltip = text.ui.productSensitivityTooltip;
  const config: Record<SensitivityLevelEnum, SensitivityConfig> = {
    [SensitivityLevelEnum.LOW]: { label: text.ui.productLibrarySensitivityLow, className: 'bg-sseeaa/10 text-sseeaa', tooltip },
    [SensitivityLevelEnum.MEDIUM]: { label: text.ui.productLibrarySensitivityMedium, className: 'bg-sseeaa/10 text-sseeaa', tooltip },
    [SensitivityLevelEnum.HIGH]: { label: text.ui.productLibrarySensitivityHigh, className: 'bg-sseeaa/10 text-sseeaa', tooltip },
    [SensitivityLevelEnum.NORMAL]: { label: text.ui.productLibrarySensitivityNormal, className: 'bg-g/10 text-g', tooltip },
    [SensitivityLevelEnum.REJECTED]: { label: text.ui.productSensitivityRejected, className: 'bg-red-50 text-red-500', tooltip }
  };
  return level ? config[level] : config[SensitivityLevelEnum.NORMAL];
};

export interface AttributeBadgeConfig {
  label: string;
  className: string;
  tooltip: string;
}

/**
 * Get battery level badge configuration
 */
export const getBatteryConfig = (level?: BatteryLevelEnum, sensitivityLevel?: SensitivityLevelEnum): AttributeBadgeConfig | null => {
  const text = getProductLibraryTemplate(getPreferredLanguage());
  if (level === BatteryLevelEnum.LESS_THAN_100WH) {
    const sensitivityConfig = sensitivityLevel ? getSensitivityConfig(sensitivityLevel) : null;
    const className = sensitivityConfig?.className || 'bg-gray-100 text-gray-600';

    return {
      label: text.ui.productAttributeBatteryWeak,
      className,
      tooltip: text.ui.productAttributeBatteryTooltip
    };
  }
  return null;
};

/**
 * Get magnetic level badge configuration
 */
export const getMagneticConfig = (level?: MagneticLevelEnum, sensitivityLevel?: SensitivityLevelEnum): AttributeBadgeConfig | null => {
  const text = getProductLibraryTemplate(getPreferredLanguage());
  if (level === MagneticLevelEnum.WEAK) {
    const sensitivityConfig = sensitivityLevel ? getSensitivityConfig(sensitivityLevel) : null;
    const className = sensitivityConfig?.className || 'bg-gray-100 text-gray-600';

    return {
      label: text.ui.productAttributeMagneticWeak,
      className,
      tooltip: text.ui.productAttributeMagneticTooltip
    };
  }
  return null;
};
